import { normalizePageCustomCss } from "@/lib/cms-puck/blocks/page-custom-css";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { css } from "@codemirror/lang-css";
import { EditorView } from "@codemirror/view";
import CodeMirror from "@uiw/react-codemirror";
import { useMemo } from "react";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";

const editorTheme = EditorView.theme({
  "&": {
    fontSize: "12px",
    backgroundColor: "#f8fafc",
  },
  ".cm-content": {
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    lineHeight: "1.6",
    padding: "12px 0",
  },
  ".cm-gutters": {
    backgroundColor: "#f1f5f9",
    color: "#94a3b8",
    borderRight: "1px solid #e2e8f0",
  },
  ".cm-activeLine": {
    backgroundColor: "rgba(148, 163, 184, 0.12)",
  },
  "&.cm-focused .cm-cursor": {
    borderLeftColor: "#334155",
  },
  "&.cm-focused .cm-selectionBackground, .cm-selectionBackground": {
    backgroundColor: "rgba(59, 130, 246, 0.2) !important",
  },
});

/**
 * Éditeur du CSS personnalisé de page (`root.props.customCss`).
 * Cible les ID / classes posés sur les blocs (onglet Avancé → Attributs).
 */
export function PageCustomCssField({
  value,
  onChange,
}: {
  value: string | null | undefined;
  onChange: (value: string) => void;
}) {
  const cmsNew = useCmsNew();
  const cssValue = normalizePageCustomCss(value);

  const extensions = useMemo(
    () => [css(), editorTheme, EditorView.lineWrapping],
    [],
  );

  return (
    <div className="space-y-3 p-1">
      <div className="space-y-1">
        <Label className="text-sm font-medium text-slate-700">
          {cmsNew("cms.new.fields.pageCustomCss.title", "CSS personnalisé")}
        </Label>
        <p className="text-xs leading-relaxed text-slate-500">
          {cmsNew(
            "cms.new.fields.pageCustomCss.hintBefore",
            "Règles CSS appliquées sur cette page uniquement (site publié et aperçu). Utilisez les",
          )}{" "}
          <strong>ID</strong>{" "}
          {cmsNew("cms.new.fields.pageCustomCss.hintAnd", "et")}{" "}
          <strong>{cmsNew("cms.new.fields.pageCustomCss.hintCssClasses", "classes CSS")}</strong>{" "}
          {cmsNew("cms.new.fields.pageCustomCss.hintDefinedIn", "définis dans l'onglet")}{" "}
          <strong>{cmsNew("cms.new.auto.text.avance_attributs", "Avancé → Attributs")}</strong>{" "}
          {cmsNew("cms.new.fields.pageCustomCss.hintEachBlock", "de chaque bloc.")}
        </p>
      </div>
      <div
        className={cn(
          "overflow-hidden rounded-lg border border-slate-200",
          "focus-within:border-slate-400 focus-within:ring-2 focus-within:ring-slate-200",
        )}
      >
        <CodeMirror
          value={cssValue}
          height="320px"
          extensions={extensions}
          onChange={onChange}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: true,
            autocompletion: true,
            bracketMatching: true,
            indentOnInput: true,
            tabSize: 2,
          }}
          placeholder={`#mon-ancre {\n  scroll-margin-top: 5rem;\n}\n\n.ma-section {\n  padding-top: 2rem;\n}`}
        />
      </div>
      <p className="text-[11px] text-slate-400">
        {cmsNew(
          "cms.new.fields.pageCustomCss.footnote",
          "Les règles sont chargées dans une feuille de style dédiée à la page. Les imports externes (@import) ne sont pas autorisés.",
        )}
      </p>
    </div>
  );
}
